import { __awaiter } from "tslib";
import { derived, writable } from 'svelte/store';
import robotApiHandler from '../api/robot';
import worldApiHandler from '../api/world';
import { DebugError } from './DebugError';
/**
 * DebuggerAction - what is the debugger doing right now. Descriptive.
 */
export var DebuggerAction;
(function (DebuggerAction) {
    DebuggerAction[DebuggerAction["idle"] = 0] = "idle";
    DebuggerAction[DebuggerAction["running"] = 1] = "running";
    DebuggerAction[DebuggerAction["paused"] = 2] = "paused";
})(DebuggerAction || (DebuggerAction = {}));
;
/**
 * DebuggerMode - what mode have we asked the debugger to run in. Prescriptive.
 */
export const DebuggerMode = {
    // Modes which map to debugger.py commands
    run: { do: 'run' },
    stepOver: { do: 'step_over' },
    stepIn: { do: 'step_in' },
    stepOut: { do: 'step_out' },
    stop: { do: 'stop' },
    // Modes which don't map to debugger.py commands
    pause: { do: false },
};
const initialState = {
    action: DebuggerAction.idle,
    mode: DebuggerMode.stop,
    line: undefined,
    file: undefined,
    world: undefined,
};
var PausedReason;
(function (PausedReason) {
    PausedReason["started"] = "started";
    PausedReason["mode"] = "mode";
    PausedReason["time"] = "time";
})(PausedReason || (PausedReason = {}));
;
/**
 * Debugger state and derived helpers.
 */
export const dbgState = writable(initialState);
export const dbgIsRunning = derived(dbgState, state => state.action === DebuggerAction.running);
export const dbgIsPaused = derived(dbgState, state => state.action === DebuggerAction.paused);
export const dbgIsIdle = derived(dbgState, state => state.action === DebuggerAction.idle);
export const dbgModePausing = derived(dbgState, state => state.mode === DebuggerMode.pause);
/**
 * Useful tools for getting error information.
 */
export const errorLine = derived(dbgState, state => state.error ? state.error.line : undefined);
let currentState = initialState;
dbgState.subscribe(s => currentState = s);
/**
 * Start debugging the given code.
 */
export function dbgStart(world, pauseOnStart) {
    return __awaiter(this, void 0, void 0, function* () {
        yield initPromise;
        world.resetLevel();
        if (world.checkEndCondition()) {
            return;
        }
        dbgState.set(Object.assign(Object.assign({}, initialState), { action: DebuggerAction.running, mode: pauseOnStart ? DebuggerMode.pause : DebuggerMode.run, world }));
        const wrappedCode = ('import _debugger\n' +
            'import Coldbrew\n' +
            'from kitchen import world\n' +
            'from pathlib import Path\n' +
            'Path("/player").mkdir(exist_ok=True)\n' +
            'fp=open("/player/code.py","wb")\n' +
            'fp.write(base64.b64decode("' + btoa(world.code) + '"))\n' +
            'fp.close()\n' +
            'world.init()\n' +
            '_debugger.Debugger(' +
            '["/player/"],' +
            (pauseOnStart ? 'True' : 'False') +
            ').run(' +
            '"/player/code.py"' +
            ')');
        const resetState = () => dbgState.update(s => (Object.assign(Object.assign({}, s), { action: DebuggerAction.idle, line: undefined, file: undefined, world: undefined })));
        window.Coldbrew.runAsync(wrappedCode)
            .catch((err) => {
            world.stopTicking();
            resetState();
        })
            .then(() => __awaiter(this, void 0, void 0, function* () {
            if (!currentState.error) {
                yield world.run(1);
            }
            world.stopTicking();
            resetState();
        }));
    });
}
/**
 * Clear debugger error.
 */
export function resetDebugError() {
    dbgState.update(s => (Object.assign(Object.assign({}, s), { error: undefined })));
}
/**
 * Step in pressed.
 */
export function dbgStepIn() {
    setMode(DebuggerMode.stepIn);
}
/**
 * Step out pressed.
 */
export function dbgStepOut() {
    setMode(DebuggerMode.stepOut);
}
/**
 * Step over pressed.
 */
export function dbgStepOver() {
    setMode(DebuggerMode.stepOver);
}
/**
 * Resume pressed
 */
export function dbgResume() {
    setMode(DebuggerMode.run);
}
/**
 * Stop the current debugger session.
 */
export function dbgStop() {
    setMode(DebuggerMode.stop);
}
/**
 * Pause the debugger.
 */
export function dbgPause() {
    setMode(DebuggerMode.pause);
}
/**
 * Return a list of all known local and global names from the last state.
 */
export function getDefinedNames() {
    const state = currentState;
    if (!state.lastState) {
        return [];
    }
    return Object.keys(state.lastState.frames.reduce((names, frame) => {
        Object.keys(frame.locals).forEach(name => names[name] = true);
        Object.keys(frame.globals).forEach(name => names[name] = true);
        return names;
    }, {}));
}
/**
 * Set the desired mode. If the debugger is waiting on an instruction and the new
 * mode contains an instruction, send it.
 */
function setMode(mode) {
    dbgState.update(s => (Object.assign(Object.assign({}, s), { mode })));
    if (sendPendingResponse && mode.do) {
        sendPendingResponse({ do: mode.do });
    }
}
/**
 * Initialize Coldbrew, keep a promise to resolve before running code.
 */
const initPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
    yield window.Coldbrew.load({
    // threadWorkers: 4,
    });
    // Write modules to the Coldbrew virtual storage.
    const modulesZip = yield fetchModules();
    const b64Modules = btoa(String.fromCharCode(...modulesZip));
    yield window.Coldbrew.runAsync('import base64\n' +
        'fp=open("/pitk.zip","wb")\n' +
        'fp.write(base64.b64decode("' + b64Modules + '"))\n' +
        'fp.close()\n' +
        'import sys\n' +
        'sys.path.insert(0,"/pitk.zip")');
}))();
/**
 * Handler called when the debugger sends a message
 */
window.pitkDebugMessage = (message, data) => {
    const [system, type] = message.split('.', 2);
    switch (system) {
        case 'debug':
            return handleDebugMessage(type, data);
        default:
            console.log(message, data);
    }
};
/**
 * Handle debug.* message.
 */
function handleDebugMessage(type, data) {
    switch (type) {
        case 'state':
            return handleStateMessage(data);
        case 'crashed':
            return handleCrashedMessage(data);
        default:
            console.log('debug.' + type, data);
    }
}
;
/**
 * Handle a 'state' message from the debugger.
 */
function handleStateMessage(data) {
    const topFrame = data.frames[0];
    dbgState.update(s => (Object.assign(Object.assign({}, s), { line: topFrame.line, file: topFrame.file, lastState: data })));
}
/**
 * Handle a 'crashed' message from the debugger.
 */
function handleCrashedMessage(data) {
    const error = new DebugError(data.errorType, data.error, data.details, data.stack);
    dbgState.update(s => (Object.assign(Object.assign({}, s), { error })));
}
/**
 * Handler called when the debugger wants a command prompt
 */
let sendPendingResponse;
window.pitkDebugPrompt = (message, data) => __awaiter(void 0, void 0, void 0, function* () {
    const [system, type] = message.split('.', 2);
    try {
        switch (system) {
            case 'debug':
                return yield handleDebugPrompt(type, data);
            case 'robot':
                return yield robotApiHandler(currentState.world, type, data);
            case 'world':
                return yield worldApiHandler(currentState.world, type, data);
            default:
                console.log(message, data);
                return {};
        }
    }
    catch (err) {
        return {
            error: err.message,
            helpText: err.helpText || null,
        };
    }
});
/**
 * Handle any debug.* message.
 */
function handleDebugPrompt(type, data) {
    return __awaiter(this, void 0, void 0, function* () {
        switch (type) {
            case 'paused':
                return handlePaused(data);
            default:
                console.log('debug.' + type, data);
                return {};
        }
    });
}
/**
 * Handle a 'paused' prompt from the debugger.
 */
function handlePaused(data) {
    return __awaiter(this, void 0, void 0, function* () {
        if (data.reason === PausedReason.mode) {
            // Pausing because of mode (i.e.: step in/out/over complete).
            setMode(DebuggerMode.pause);
        }
        else if (currentState.mode.do) {
            // If current mode has a 'do' command, send it.
            // i.e.: If stopping because of time, keep going.
            return { do: currentState.mode.do };
        }
        dbgState.update(s => (Object.assign(Object.assign({}, s), { action: DebuggerAction.paused })));
        return yield new Promise((resolve) => {
            sendPendingResponse = (result) => {
                sendPendingResponse = undefined;
                dbgState.update(s => (Object.assign(Object.assign({}, s), { action: DebuggerAction.running })));
                resolve(result);
            };
        });
    });
}
/**
 * Grab modules zipfile to install.
 */
function fetchModules() {
    return __awaiter(this, void 0, void 0, function* () {
        const result = yield fetch('/build/modules.zip');
        if (!result.ok) {
            throw new Error('Failed to load pitk modules: ' + result.status);
        }
        return new Uint8Array(yield result.arrayBuffer());
    });
}
